import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { QRCodeCanvas as QRCode } from 'qrcode.react';
import api, { API_URL } from '../lib/api';
import { saveSession, isAuthenticated } from '../lib/auth';
import { disconnectSocket } from '../lib/socket';
import { applyTheme } from '../lib/prefs';
import logo from '../assets/nearits_logo.png';

// QR login. Shows a QR, the mobile app scans it (Phase 0 connectSession mints a
// scoped web token), then this page pulls the token from /session-data and
// routes into the messenger. Uses its own short-lived socket for the QR events
// — the authenticated messenger socket is created later, with the token.
export default function LoginPage() {
  const navigate = useNavigate();
  const [qrToken, setQrToken] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | waiting | connecting | error

  const createQRCode = useCallback(async () => {
    try {
      setStatus('loading');
      const { data } = await api.get('/api/qr/create-qr');
      setQrToken(data.qrToken);
      setStatus('waiting');
    } catch (err) {
      console.error('Error creating QR:', err);
      setStatus('error');
    }
  }, []);

  const finishLogin = useCallback(
    async (token) => {
      setStatus('connecting');
      try {
        const { data } = await api.get(`/api/qr/session-data/${token}`);
        if (data?.success && data.token) {
          saveSession({
            token: data.token,
            userId: data.sessionData?.userId,
            userName: data.sessionData?.userName,
            qrToken: token,
          });
          // Drop any cached anonymous socket so the messenger re-handshakes
          // with the new token.
          disconnectSocket();
          navigate('/app', { replace: true });
          return;
        }
        throw new Error('No token in session data');
      } catch (err) {
        console.error('Failed to retrieve web token:', err);
        setStatus('error');
      }
    },
    [navigate]
  );

  // Already logged in? Skip straight to the app.
  useEffect(() => {
    applyTheme();
    if (isAuthenticated()) navigate('/app', { replace: true });
    else createQRCode();
  }, [navigate, createQRCode]);

  // Auto-refresh the QR before the backend's 10-minute session TTL expires, so
  // the code on screen is always scannable.
  useEffect(() => {
    if (!qrToken || status !== 'waiting') return undefined;
    const t = setTimeout(() => createQRCode(), 9 * 60 * 1000);
    return () => clearTimeout(t);
  }, [qrToken, status, createQRCode]);

  // Listen for the scan over a dedicated QR socket.
  useEffect(() => {
    if (!qrToken) return undefined;
    const socket = io(API_URL, { transports: ['websocket'] });
    socket.emit('register-qr-session', qrToken);

    const onConnected = (payload) => {
      if (payload.qrToken === qrToken) finishLogin(qrToken);
    };
    socket.on('session-connected', onConnected);

    return () => {
      socket.off('session-connected', onConnected);
      socket.disconnect();
    };
  }, [qrToken, finishLogin]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
        <img
          src={logo}
          alt="NearIts"
          className="mx-auto mb-4 h-20 w-20 rounded-2xl object-cover"
        />
        <h1 className="text-2xl font-semibold text-ink">NearIt Web</h1>
        <p className="text-muted mt-1">Chat from your computer</p>

        <div className="mt-8 flex flex-col items-center">
          {status === 'connecting' ? (
            <div className="py-16 text-brand font-medium">Establishing secure session…</div>
          ) : qrToken ? (
            <div className="p-4 bg-white rounded-xl border border-slate-200">
              <QRCode value={qrToken} size={232} />
            </div>
          ) : (
            <div className="py-16 text-muted">Generating QR…</div>
          )}
        </div>

        <div className="mt-8 text-left text-sm text-muted bg-slate-50 rounded-xl p-4 leading-relaxed">
          <strong className="text-ink">How to connect</strong>
          <br />
          1. Open NearIt on your phone
          <br />
          2. Go to <span className="text-ink">Link Device</span>
          <br />
          3. Scan this QR code
        </div>

        {status === 'error' && (
          <p className="mt-4 text-sm text-red-500">
            Something went wrong. Please refresh the code.
          </p>
        )}

        <button
          onClick={createQRCode}
          className="mt-6 inline-flex items-center gap-2 text-brand font-medium hover:text-brand-dark"
        >
          ⟳ Refresh QR code
        </button>
      </div>
    </div>
  );
}
